import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Card, CardContent } from '@/components/ui/card'
import { PageHeader } from '@/components/app/PageHeader'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { Clock, ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  type ChangelogData,
  changelogColorStyle,
  changelogTextColor,
  getChangelogIcon,
} from '@/features/admin/lib/changelog'
import { FallBeamBackground } from '@/components/backgrounds/FallBeamBackground'



function scrollToEntry(id: string) {
  const el = document.getElementById(id)
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
}

export function ChangelogPage() {
  const { t, i18n } = useTranslation()
  const [data, setData] = useState<ChangelogData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [visibleCount, setVisibleCount] = useState(5)
  useEffect(() => {
    const lang = i18n.language === 'fr' ? 'fr' : 'en'
    fetch(`/changelog-${lang}.json?v=${Date.now()}`)
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch changelog')
        return res.json()
      })
      .then((json: ChangelogData) => {
        setData(json)
        setLoading(false)
      })
      .catch(() => {
        setError(true)
        setLoading(false)
      })
  }, [i18n.language])

  const sortedEntries = useMemo(() => {
    if (!data) return []
    return [...data.entries].sort((a, b) => {
      const idDiff = parseInt(b.id, 10) - parseInt(a.id, 10)
      if (idDiff !== 0) return idDiff
      const timeA = new Date(`${a.date}T${a.time.replace(' UTC+1', '')}`).getTime()
      const timeB = new Date(`${b.date}T${b.time.replace(' UTC+1', '')}`).getTime()
      return timeB - timeA
    })
  }, [data])

  const visibleEntries = sortedEntries.slice(0, visibleCount)
  const hasMore = visibleCount < sortedEntries.length



  const latestColor = data?.entries[0]?.color

  if (loading) {
    return (
      <div className="relative">
        <FallBeamBackground beamColorClass={latestColor ?? 'slate'} className="opacity-40" lineCount={40} />
        <div className="relative mx-auto max-w-3xl space-y-5">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="relative">
        <FallBeamBackground beamColorClass={latestColor ?? 'slate'} className="opacity-40" lineCount={40} />
        <div className="relative flex h-64 items-center justify-center rounded-xl border border-dashed mx-auto max-w-3xl">
          <p className="text-sm text-muted-foreground">{t('changelog.error')}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="relative">
      <FallBeamBackground beamColorClass={latestColor ?? 'cyan-400'} lineCount={40} />

      <div className="relative mx-auto max-w-6xl">
        <div className="space-y-1">
          <PageHeader title={t('changelog.title')} />
          <p className="text-sm text-muted-foreground">
            {t('changelog.description')}
          </p>
        </div>

        <div className="mt-5 grid gap-8 lg:grid-cols-[1fr_240px]">
          {/* Main changelog entries */}
          <div className="flex flex-col gap-5">
            {visibleEntries.map((entry) => {
              const Icon = getChangelogIcon(entry.type)
              const style = changelogColorStyle(entry.color)
              const formattedDate = new Intl.DateTimeFormat(
                i18n.language === 'fr' ? 'fr-FR' : 'en-US',
                { day: 'numeric', month: 'long', year: 'numeric' },
              ).format(new Date(entry.date))

              return (
                <Card
                  key={entry.id}
                  id={`changelog-${entry.id}`}
                  className="scroll-mt-24 rounded-lg border-0 bg-card/80 shadow-none app-card-padding backdrop-blur-sm"
                >
                  <CardContent className="p-0">
                    {/* Card Head */}
                    <div className="flex flex-wrap items-center gap-2.5">
                      {/* Type Badge with Icon inside */}
                      <span
                        className={cn(
                          'flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-semibold uppercase tracking-wider',
                          style,
                        )}
                      >
                        <Icon className="size-4" />
                        {entry.type}
                      </span>

                      {/* Timestamp */}
                      <span className="ml-auto flex items-center gap-1.5 text-xs tabular-nums text-muted-foreground">
                        <Clock className="size-3" />
                        {formattedDate}
                      </span>
                    </div>

                    {/* Title */}
                    <h3 className="mt-3 text-base font-semibold tracking-tight text-foreground">
                      {entry.title}
                    </h3>

                    {/* Description */}
                    <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                      {entry.description}
                    </p>

                    {/* Items List — neutral text */}
                    {entry.items.length > 0 && (
                      <ul className="mt-3 grid gap-1.5">
                        {entry.items.map((item, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm">
                            <span className="mt-2 size-1 shrink-0 rounded-full bg-muted-foreground/50" />
                            <span className="text-foreground">{item}</span>
                          </li>
                        ))}
                      </ul>
                    )}

                    {/* Meta — production text left, version badge right */}
                    <div className="mt-3.5 flex items-center justify-between border-t pt-3">
                      <span className="text-xs text-muted-foreground">
                        {t('changelog.meta.production')} ·{' '}
                        <span className="capitalize">{entry.type}</span>
                      </span>
                      <span className="rounded-md bg-muted px-2.5 py-1 text-xs font-semibold tabular-nums text-muted-foreground">
                        v{entry.version}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              )
            })}

            {hasMore && (
              <Button
                variant="outline"
                size="lg"
                className="w-full"
                onClick={() => setVisibleCount((c) => c + 5)}
              >
                <ChevronDown className="mr-2 size-4" />
                {t('changelog.showMore')}
              </Button>
            )}
          </div>

          {/* Right sidebar — TOC */}
          <aside className="hidden lg:block">
            <div className="sticky top-20 max-h-[calc(100vh-6rem)] overflow-y-auto">
              <h4 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Versions
              </h4>
              <nav className="space-y-2">
                {sortedEntries.map((entry, index) => {
                  const Icon = getChangelogIcon(entry.type)
                  const iconColor = changelogTextColor(entry.color)
                  const isVisible = index < visibleCount
                  return (
                    <button
                      key={entry.id}
                      type="button"
                      onClick={() => scrollToEntry(`changelog-${entry.id}`)}
                      disabled={!isVisible}
                      className={cn(
                        'flex w-full items-center gap-2 text-left text-xs transition-opacity',
                        isVisible ? 'cursor-pointer opacity-100 hover:opacity-80' : 'cursor-default opacity-40',
                      )}
                    >
                      <Icon className={cn('size-3 shrink-0', iconColor)} />
                      <span className="font-semibold tabular-nums text-foreground">v{entry.version}</span>
                      <span className="truncate text-muted-foreground">{entry.title}</span>
                    </button>
                  )
                })}
              </nav>
            </div>
          </aside>
        </div>
      </div>
    </div>
  )
}
